/**
 * Depo günü istemcisi (`/depo`, ADR 2026-10-21-depo-gunu).
 *
 * Bu dosya yalnız istek atar ve yanıtı tipler; hiçbir adet, fark ya da eşik burada hesaplanmaz.
 * Okuma yanıtının sözleşmesi `@aquabox/shared` içindeki `DepotDayResponse`'tır.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { DepotDayResponse } from '@aquabox/shared';
import { api } from './client';

/** Sunucu yanıtının yalnız ekranın kullandığı alanları. */
export interface CountWindowRow {
  kind: string;
  productId: string;
  condition: 'FULL' | 'EMPTY';
  /** Beklenen ile sayılan arasındaki fark (sunucu hesabı). */
  countDiff: number;
  opening: boolean;
}

/** Pencerede dokunan defter hareketi (sunucu `context.moves`); plaka yoksa null. */
export interface CountWindowMove {
  id: string;
  type: string;
  productId: string;
  condition: 'FULL' | 'EMPTY';
  qty: number;
  occurredAt: string; // ISO UTC
  plate: string | null;
}

export interface CountWindow {
  target: { id: string };
  delta: CountWindowRow[];
  adjustmentWarning: string | null;
  context: { moves: CountWindowMove[] };
}

export interface DepotCountLine {
  kind: 'JUG_19L' | 'JUG_15L_GLASS' | 'CASE_05L';
  productId: string;
  condition: 'FULL' | 'EMPTY';
  qty: number;
}

export interface DepotCountBody {
  clientId: string;
  scope: 'DEPOT';
  countedAt: string;
  lines: DepotCountLine[];
}

export interface ReloadBody {
  tripId: string;
  direction: 'ADD' | 'REMOVE';
  lines: { productId: string; qty: number }[];
  clientId: string;
}

/** Sabah dolumu: `POST /api/trips/:id/load` gövdesi (sunucu şeması `loadSchema`). */
export interface TripLoadBody {
  lines: { productId: string; qty: number }[];
  clientId: string;
}

export interface ShipmentReceiveBody {
  brandId: string;
  productId: string;
  waybillNo: string;
  qtyFull: number;
  qtyEmptyOut: number;
  countedFull?: number;
  countedEmptyOut?: number;
  foreign?: { brandName: string; qty: number }[];
  clientId: string;
}

export interface ShipmentReceiveResult {
  shipment: { id: string };
  /** Sayılan adet irsaliyeden farklıysa yazılan ADJUSTMENT satırları. */
  adjustments: { condition: 'FULL' | 'EMPTY'; qty: number }[];
  foreignReturned: number;
}

/** Gün özeti. `day` Europe/Istanbul gün anahtarıdır (YYYY-MM-DD). */
export function useDepotDay(day: string) {
  return useQuery({
    queryKey: ['depot-day', day],
    queryFn: () => api.get<DepotDayResponse>(`/api/stock/depot-day?day=${day}`),
  });
}

async function refresh(qc: QueryClient, keys: readonly string[]): Promise<void> {
  for (const key of keys) await qc.invalidateQueries({ queryKey: [key] });
}

/** Akşam sayımı. Sayım deftere yazmaz; yanıtta yalnız kayıt kimliği önemlidir. */
export function useCreateDepotCount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DepotCountBody) => api.post<{ id: string }>('/api/stock/counts', body),
    onSuccess: () => refresh(qc, ['depot-day']),
  });
}

/** Sayımın penceresi: fark satırları ve bağlam. Yalnız sayım kimliği verildiğinde okunur. */
export function useCountWindow(id: string | null) {
  return useQuery({
    queryKey: ['stock-count-window', id],
    queryFn: () => api.get<CountWindow>(`/api/stock/counts/${id}/window`),
    enabled: id !== null,
  });
}

/**
 * Sabah dolumu: yola çıkmamış sefere (OPENED/LOADING) yükleme yazar.
 * Gövde sunucunun `loadSchema`'sı ile birebir aynıdır; `clientId` çevrimdışı kuyruk anahtarıdır.
 */
export function useCreateTripLoad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { tripId: string; body: TripLoadBody }) =>
      api.post(`/api/trips/${v.tripId}/load`, v.body),
    onSuccess: () => refresh(qc, ['depot-day', 'stock-vehicles', 'stock-moves', 'trips']),
  });
}

/** Patron ara yüklemesi: sunucu kaydı onaylı yazar; depo ve araç stokları değişir. */
export function useCreateReload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReloadBody) => api.post('/api/reload-requests', body),
    onSuccess: () =>
      refresh(qc, [
        'depot-day',
        'reload-requests',
        'stock-levels',
        'stock-vehicles',
        'stock-moves',
      ]),
  });
}

/** Sevkiyat teslim alma: irsaliye ve sayılan adetler sunucuda defterle karşılaştırılır. */
export function useReceiveShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShipmentReceiveBody) =>
      api.post<ShipmentReceiveResult>('/api/stock/shipments/receive', body),
    onSuccess: () =>
      refresh(qc, ['depot-day', 'stock-levels', 'stock-vehicles', 'stock-moves', 'stock-foreign']),
  });
}
